import { useState } from "react";
import { Link } from "react-router";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { ApiError } from "../../api/client";
import {
  getHomeDefaults,
  type HomeWidget,
  saveHomeDefaults,
} from "../../api/hub/home";
import type { Role } from "../../api/hub/types";
import { ConfigPanel } from "./ConfigPanel";
import { HomeGrid } from "./HomeGrid";
import { HomeLibrary } from "./HomeLibrary";
import { DataState, WorkDeskHeader } from "./Frame";
import { homeKeys, message, useWorkspaceMe } from "./data";
import { addWidget, localLayoutError } from "./layout";
import { useHomeEditing } from "./useHomeEditing";
export function HomeDefaultsPage() {
  const [role, setRole] = useState<Role>("member"),
    [library, setLibrary] = useState(false),
    [configuring, setConfiguring] = useState<HomeWidget | null>(null);
  const [busy, setBusy] = useState(false),
    [error, setError] = useState(""),
    [notice, setNotice] = useState(""),
    [forcedConflict, setForcedConflict] = useState(false);
  const me = useWorkspaceMe(),
    editor = useHomeEditing(),
    client = useQueryClient(),
    allowed = me.data?.data.role === "CEO";
  const query = useQuery({
    queryKey: homeKeys.defaults(role),
    queryFn: () => getHomeDefaults(role),
    enabled: allowed,
    refetchInterval: 1500,
  });
  const state = query.data,
    descriptors = state?.widgets ?? [],
    draft = editor.draft;
  const conflict = forcedConflict ||
    !!(draft && state &&
      (draft.baseVersion !== state.version ||
        draft.baseLayout !== JSON.stringify(state.layout)));
  const save = async () => {
    if (!draft || !state || conflict) return;
    const invalid = localLayoutError(draft.layout, descriptors);
    if (invalid) {
      setError(invalid);
      return;
    }
    setBusy(true);
    setError("");
    setNotice("");
    try {
      const next = await saveHomeDefaults(
        role,
        draft.layout,
        draft.baseVersion,
      );
      editor.setDraft(null);
      setForcedConflict(false);
      client.setQueryData(homeKeys.defaults(role), next);
      await client.invalidateQueries({ queryKey: homeKeys.state() });
      setNotice(role + " 默认主页已保存，未定制个人主页的同事会跟随这个布局。");
    } catch (e) {
      setError(message(e));
      if (e instanceof ApiError && e.status === 409) {
        setForcedConflict(true);
        await query.refetch();
      }
    } finally {
      setBusy(false);
    }
  };
  return (
    <main className="home-page">
      <WorkDeskHeader
        title="给每个角色一张可直接用的工作桌。"
        mode="中台设置 · 角色默认"
        description="由 CEO 在网页维护。个人定制只覆盖自己的主页，标准仪表盘始终固定。"
        actions={<Link to="/settings/platform">回到中台设置 ↗</Link>}
      />
      <DataState query={me}>
        {me.isSuccess && !allowed
          ? (
            <p className="home-error" role="alert">
              只有 CEO 可以设置角色默认主页。你可以编辑自己的主页。
            </p>
          )
          : allowed && (
            <>
              <div className="home-scope-strip">
                <label className="home-inline-field">
                  默认主页的角色<select
                    value={role}
                    disabled={!!draft || busy}
                    onChange={(e) => {
                      setRole(e.target.value as Role);
                      setError("");
                      setNotice("");
                      setForcedConflict(false);
                    }}
                    aria-label="选择默认主页角色"
                  >
                    <option value="CEO">CEO</option>
                    <option value="CPO">CPO</option>
                    <option value="CTO">CTO</option>
                    <option value="member">成员</option>
                  </select>
                </label>
                <span>保存对象：{role} 角色默认 · 不修改任何人的个人配置</span>
              </div>
              {editor.navigationBlocked && editor.draft && (
                <p className="home-notice" role="status">
                  草稿尚未保存，请先保存或取消，再离开工作桌。
                </p>
              )}
              <p className="home-muted">
                预览按你当前的 CEO
                身份读取数据；同事使用时按各自身份与权限取数。
              </p>
              {notice && <p className="home-notice" role="status">{notice}</p>}
              {error && <p className="home-error" role="alert">{error}</p>}
              <DataState query={query}>
                {state && (
                  <>
                    {!draft
                      ? (
                        <div className="home-actions home-editor-entry">
                          <span className="home-muted">
                            {state.version === 0
                              ? "尚未配置，显示系统默认"
                              : role + " 默认 v" + state.version}
                          </span>
                          <button
                            className="home-primary"
                            disabled={editor.mobile || busy}
                            onClick={() => {
                              editor.begin(state.layout, state.version);
                              setError("");
                              setNotice("");
                            }}
                          >
                            编辑 {role} 默认主页
                          </button>
                          {editor.mobile && (
                            <p>请在桌面编辑角色默认，手机可以查看。</p>
                          )}
                        </div>
                      )
                      : (
                        <div className="home-editor-bar">
                          <div>
                            <strong>
                              正在编辑 {role} 角色默认 ·{" "}
                              {draft.layout.widgets.length} / 24 个部件
                            </strong>
                            <p>同一编辑器，不开放给外部 AI。</p>
                          </div>
                          <div className="home-actions">
                            <button
                              disabled={busy || editor.mobile}
                              onClick={() => setLibrary(true)}
                            >
                              ＋ 部件库
                            </button>
                            <button
                              disabled={busy}
                              onClick={() => {
                                editor.setDraft(null);
                                setForcedConflict(false);
                                setError("");
                                setNotice("已取消角色默认草稿。");
                              }}
                            >
                              取消编辑
                            </button>
                            <button
                              className="home-primary"
                              disabled={busy || conflict || editor.mobile}
                              onClick={() => void save()}
                            >
                              {busy ? "正在保存…" : "保存角色默认新版本"}
                            </button>
                          </div>
                        </div>
                      )}
                    {conflict && draft && (
                      <section className="home-conflict" role="alert">
                        <strong>这个角色默认已更新，草稿已保留。</strong>
                        <p>
                          草稿基于 v{draft.baseVersion}，当前 v{state
                            .version}。选择载入最新，或保留草稿继续。
                        </p>
                        <div className="home-actions">
                          <button
                            onClick={() => {
                              editor.begin(state.layout, state.version);
                              setForcedConflict(false);
                              setError("");
                            }}
                          >
                            放弃草稿，载入最新
                          </button>
                          <button
                            onClick={() => {
                              editor.setDraft({
                                ...draft,
                                baseVersion: state.version,
                                baseLayout: JSON.stringify(state.layout),
                              });
                              setForcedConflict(false);
                              setError("");
                            }}
                          >
                            保留草稿，在最新版本继续
                          </button>
                        </div>
                      </section>
                    )}
                    <HomeGrid
                      layout={draft?.layout ?? state.layout}
                      descriptors={descriptors}
                      sharedOnly
                      editing={!!draft && !editor.mobile}
                      onChange={editor.change}
                      onConfigure={setConfiguring}
                    />
                  </>
                )}
              </DataState>
              {library && draft && (
                <HomeLibrary
                  descriptors={descriptors}
                  count={draft.layout.widgets.length}
                  onClose={() => setLibrary(false)}
                  onAdd={(descriptor) => {
                    try {
                      editor.change(addWidget(draft.layout, descriptor));
                    } catch (e) {
                      setError(message(e));
                    }
                    setLibrary(false);
                  }}
                />
              )}
              {configuring && draft && descriptors.find((item) =>
                item.type === configuring.type
              ) && (
                <ConfigPanel
                  widget={configuring}
                  descriptor={descriptors.find((item) =>
                    item.type === configuring.type
                  )!}
                  onClose={() => setConfiguring(null)}
                  onApply={(config) =>
                    editor.change({
                      ...draft.layout,
                      widgets: draft.layout.widgets.map((widget) =>
                        widget.id === configuring.id
                          ? { ...widget, config }
                          : widget
                      ),
                    })}
                />
              )}
            </>
          )}
      </DataState>
    </main>
  );
}
